import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import './Sidebar.css';
import {
  FaStickyNote,
  FaFileInvoiceDollar,
  FaListAlt,
  FaTachometerAlt,
  FaClipboardList,
  FaIdCard,
  FaUsers,
  FaTags,
  FaLayerGroup,
  FaFileInvoice,
  FaMoneyBill,
  FaMoneyBillWave,
  FaFileAlt,
  FaExchangeAlt,
  FaCog,
  FaUserTie,
  FaBook,
  FaChartPie,
  FaCalendarAlt,
  FaBalanceScale,
  FaChartLine,
  FaDoorOpen,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';

// Academic Year Switcher Component
// removed duplicate import of useEffect, useState
import axios from 'axios';
import appConstants from '../constants/appConstants';

function AcademicYearSwitcher() {
  const { selectedAcademicYear, setSelectedAcademicYear } = useAppContext();
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    async function fetchYears() {
      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.get(
          `${appConstants.API_ROOT_URL}get_academic_years`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (response.data.status === 'success') {
          const newYears = response.data.data || [];
          setAcademicYears(newYears);
          // Update selectedAcademicYear if needed
          if (selectedAcademicYear) {
            const found = newYears.find(
              (y) => y.id === selectedAcademicYear.id,
            );
            if (!found && newYears.length > 0) {
              // If deleted, select first available
              setSelectedAcademicYear({
                id: newYears[0].id,
                name: newYears[0].name,
                start_date: newYears[0].start_date,
                end_date: newYears[0].end_date,
              });
            } else if (found) {
              // If edited, update to latest
              setSelectedAcademicYear({
                id: found.id,
                name: found.name,
                start_date: found.start_date,
                end_date: found.end_date,
              });
            }
            if (!found && newYears.length === 0) {
              setSelectedAcademicYear(null);
            }
          } else if (newYears.length > 0) {
            // If nothing selected, select first
            setSelectedAcademicYear({
              id: newYears[0].id,
              name: newYears[0].name,
              start_date: newYears[0].start_date,
              end_date: newYears[0].end_date,
            });
          }
        }
      } catch (err) {
        setAcademicYears([]);
      } finally {
        setLoading(false);
      }
    }
    fetchYears();
    // Listen for academicYearsChanged event to refresh list
    const handler = () => fetchYears();
    window.addEventListener('academicYearsChanged', handler);
    return () => {
      window.removeEventListener('academicYearsChanged', handler);
    };
  }, []);

  if (loading)
    return (
      <div
        style={{
          textAlign: 'center',
          marginBottom: 8,
          fontSize: '0.95em',
          color: '#b0b8c9',
        }}
      >
        Loading year...
      </div>
    );
  if (!selectedAcademicYear) return null;
  return (
    <div style={{ textAlign: 'center', marginBottom: 2 }}>
      <div style={{ display: 'inline-block', position: 'relative' }}>
        <button
          style={{
            background: 'transparent',
            color: '#8ca9b599',
            border: 'none',
            borderRadius: 6,
            padding: '2px 8px',
            fontWeight: 600,
            fontSize: '0.9em',
            cursor: 'pointer',
            minWidth: 120,
          }}
          onClick={() => setOpen((o) => !o)}
        >
          {selectedAcademicYear.name}{' '}
          <span style={{ fontSize: '0.8em', marginLeft: 6 }}>
            <FaChevronDown />
          </span>
        </button>
        {open && academicYears.length > 1 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '110%',
              minWidth: 160,
              background: '#fff',
              color: '#22304a',
              border: '1px solid #dbe2ef',
              borderRadius: 6,
              boxShadow: '0 2px 8px rgba(44,54,80,0.08)',
              zIndex: 1000,
            }}
          >
            {academicYears.map((y) => (
              <div
                key={y.id}
                style={{
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontWeight: y.id === selectedAcademicYear.id ? 700 : 400,
                  background:
                    y.id === selectedAcademicYear.id
                      ? '#eaf0fa'
                      : 'transparent',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  if (y.id !== selectedAcademicYear.id) {
                    if (
                      window.confirm(
                        'Switching the academic year will reload data for the new year. Are you sure you want to continue?',
                      )
                    ) {
                      setSelectedAcademicYear({
                        id: y.id,
                        name: y.name,
                        start_date: y.start_date,
                        end_date: y.end_date,
                      });
                      setOpen(false);
                    }
                  } else {
                    setOpen(false);
                  }
                }}
              >
                {y.name}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Sidebar = () => {
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [registersOpen, setRegistersOpen] = useState(true);
  const [setupsOpen, setSetupsOpen] = useState(false);

  return (
    <div
      className="sidebar-wrapper custom-sidebar vh-100 d-flex flex-column"
      style={{
        width: '210px',
        position: 'fixed',
        boxShadow: '0 2px 16px rgba(60,72,88,0.10)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
      }}
    >
      <h4
        className="text-center"
        style={{
          fontWeight: 700,
          letterSpacing: 1,
          margin: '18px 0 4px 0',
          background:
            'linear-gradient(135deg, #ede5cc 0%, #f4e6a1 50%, #ede5cc 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        Sentry
      </h4>
      <AcademicYearSwitcher />
      <div
        className="flex-grow-1 p-2"
        style={{
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <ul className="nav flex-column">
          {/* Top Level - Always Visible */}
          <li className="nav-item">
            <Link to="/dashboard" className="nav-link text-light">
              <FaTachometerAlt className="me-2" /> Dashboard
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/attendance-management" className="nav-link text-light">
              <FaClipboardList className="me-2" /> Attendance
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/tests" className="nav-link text-light">
              <FaClipboardList className="me-2" /> Tests
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/term-exams" className="nav-link text-light">
              <FaFileAlt className="me-2" /> Exams
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/swipe-card" className="nav-link text-light">
              <FaIdCard className="me-2" /> Swipe Card
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/class-scheduler" className="nav-link text-light">
              <FaCalendarAlt className="me-2" /> Class Scheduler
            </Link>
          </li>

          <hr className="text-light my-2" />

          {/* Registers Section - Open by Default */}
          <li className="nav-item">
            <button
              className="border-0 w-100 text-start"
              style={{
                backgroundColor: 'transparent',
                color: '#60a5fa',
                fontSize: '0.85em',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                padding: '8px 16px',
              }}
              onClick={() => setRegistersOpen(!registersOpen)}
            >
              REGISTERS
              <span className="float-end">
                {registersOpen ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            </button>
          </li>
          {registersOpen && (
            <ul className="nav flex-column ps-3">
              <li className="nav-item">
                <Link to="/student-list" className="nav-link text-light">
                  <FaUsers className="me-2" /> Students
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/staff-management" className="nav-link text-light">
                  <FaUserTie className="me-2" /> Staff
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/label-management" className="nav-link text-light">
                  <FaTags className="me-2" /> Labels
                </Link>
              </li>
            </ul>
          )}

          {/* Accounting Section - Closed by Default */}
          <li className="nav-item">
            <button
              className="border-0 w-100 text-start"
              style={{
                backgroundColor: 'transparent',
                color: '#60a5fa',
                fontSize: '0.85em',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                padding: '8px 16px',
              }}
              onClick={() => setAccountingOpen(!accountingOpen)}
            >
              ACCOUNTING
              <span className="float-end">
                {accountingOpen ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            </button>
          </li>
          {accountingOpen && (
            <ul className="nav flex-column ps-3">
              <li className="nav-item">
                <Link to="/receipt" className="nav-link text-light">
                  <FaFileInvoice className="me-2" /> Receipt
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/payment" className="nav-link text-light">
                  <FaMoneyBill className="me-2" /> Payment
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/journal" className="nav-link text-light">
                  <FaFileAlt className="me-2" /> Journal
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/contra" className="nav-link text-light">
                  <FaExchangeAlt className="me-2" /> Contra
                </Link>
              </li>
              <hr className="text-light my-1" />
              <li className="nav-item">
                <Link to="/account-management" className="nav-link text-light">
                  <FaFileInvoice className="me-2" /> Accounts
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/account-types" className="nav-link text-light">
                  <FaListAlt className="me-2" /> Account Types
                </Link>
              </li>
              <hr className="text-light my-1" />
              <li className="nav-item">
                <Link to="/account-statement" className="nav-link text-light">
                  <FaFileInvoiceDollar className="me-2" /> Acc. Statement
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/fee-summary" className="nav-link text-light">
                  <FaFileInvoiceDollar className="me-2" /> Fees Summary
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/acc-type-summary" className="nav-link text-light">
                  <FaListAlt className="me-2" /> Acc. Type Summary
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/trial-balance" className="nav-link text-light">
                  <FaBalanceScale className="me-2" /> Trial Balance
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/profit-loss" className="nav-link text-light">
                  <FaChartLine className="me-2" /> Profit & Loss
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/balance-sheet" className="nav-link text-light">
                  <FaFileInvoice className="me-2" /> Balance Sheet
                </Link>
              </li>
            </ul>
          )}

          {/* Setups Section - Open by Default */}
          <li className="nav-item">
            <button
              className="border-0 w-100 text-start"
              style={{
                backgroundColor: 'transparent',
                color: '#60a5fa',
                fontSize: '0.85em',
                fontWeight: '600',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                padding: '8px 16px',
              }}
              onClick={() => setSetupsOpen(!setupsOpen)}
            >
              SETUPS
              <span className="float-end">
                {setupsOpen ? <FaChevronDown /> : <FaChevronRight />}
              </span>
            </button>
          </li>
          {setupsOpen && (
            <ul className="nav flex-column ps-3">
              <li className="nav-item">
                <Link to="/batch-management" className="nav-link text-light">
                  <FaLayerGroup className="me-2" /> Batches
                </Link>
              </li>

              <li className="nav-item">
                <Link to="/rooms" className="nav-link text-light">
                  <FaDoorOpen className="me-2" /> Rooms
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/manage-subjects" className="nav-link text-light">
                  <FaBook className="me-2" /> Subjects
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/holidays" className="nav-link text-light">
                  <FaCalendarAlt className="me-2" /> Holidays
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/academic-years" className="nav-link text-light">
                  <FaCalendarAlt className="me-2" /> Academic Years
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/fees-structure" className="nav-link text-light">
                  <FaMoneyBillWave className="me-2" /> Fees Structure
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/settings" className="nav-link text-light">
                  <FaCog className="me-2" /> Settings
                </Link>
              </li>
            </ul>
          )}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
